/**
 * Window Manager Overlay - PROJ-066 Refactored
 * Renders all floating incident panels (not dialogs)
 * Minimized window taskbar included
 */

import { useWindowManager } from '@/contexts/WindowManagerContext';
import { FloatingIncidentPanel } from './FloatingIncidentPanel';
import { MinimizedWindowsBar } from './FloatingIncidentPanel/MinimizedWindowsBar';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { IncidentEditorDialog } from './IncidentEditorDialog';

interface WindowManagerOverlayProps {
  onIncidentUpdate?: () => void;
}

export function WindowManagerOverlay({ onIncidentUpdate }: WindowManagerOverlayProps) {
  const {
    windows,
    closeWindow,
    focusWindow,
    minimizeWindow,
    restoreWindow,
    updateWindowPosition,
    updateWindowSize,
  } = useWindowManager();

  // Check if we're on desktop (for responsive fallback)
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // Get minimized and visible windows
  const minimizedWindows = windows.filter((w) => w.isMinimized);
  const visibleWindows = windows.filter((w) => !w.isMinimized);

  // Handle incident update (refreshes list)
  const handleIncidentUpdate = () => {
    onIncidentUpdate?.();
  };

  // No windows, render nothing
  if (windows.length === 0) {
    return null;
  }

  // On mobile/tablet, use modal dialog instead of floating panels
  if (!isDesktop) {
    const focusedWindow = windows.find((w) => w.isFocused);
    if (!focusedWindow) return null;

    return (
      <IncidentEditorDialog
        open={true}
        mode="edit"
        incident={focusedWindow.incident}
        onClose={() => closeWindow(focusedWindow.id)}
        onSubmit={async () => {
          handleIncidentUpdate();
        }}
      />
    );
  }

  return (
    <>
      {/* Render all visible floating panels */}
      {visibleWindows.map((windowState) => (
        <FloatingIncidentPanel
          key={windowState.id}
          windowState={windowState}
          onClose={() => closeWindow(windowState.id)}
          onFocus={() => focusWindow(windowState.id)}
          onMinimize={() => minimizeWindow(windowState.id)}
          onPositionChange={(pos) => updateWindowPosition(windowState.id, pos)}
          onSizeChange={(size) => updateWindowSize(windowState.id, size)}
          onIncidentUpdate={handleIncidentUpdate}
        />
      ))}

      {/* Minimized windows taskbar */}
      {minimizedWindows.length > 0 && (
        <MinimizedWindowsBar
          windows={minimizedWindows}
          onRestore={restoreWindow}
          onClose={closeWindow}
        />
      )}
    </>
  );
}
